import { Box, Flex, Pagination, Select, Text } from "@mantine/core";

type AppPaginationBarProps = {
  page: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  pageSizeOptions?: number[];
  /** Mantine Pagination `boundaries`: lower = more prev/next-focused (default 1). */
  boundaries?: number;
};

export default function AppPaginationBar({
  page,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10],
  boundaries = 1,
}: AppPaginationBarProps) {
  const normalizedPage = Math.max(1, Math.min(page, Math.max(totalPages, 1)));
  const start = totalItems === 0 ? 0 : (normalizedPage - 1) * pageSize + 1;
  const end = totalItems === 0 ? 0 : Math.min(normalizedPage * pageSize, totalItems);
  const canChangePageSize = !!onPageSizeChange && pageSizeOptions.length > 1;

  return (
    <Box>
      <Flex justify="center">
        <Pagination
          total={Math.max(totalPages, 1)}
          value={normalizedPage}
          onChange={onPageChange}
          color="primary"
          radius="md"
          withControls
          boundaries={boundaries}
          siblings={0}
        />
      </Flex>

      <Flex mt="sm" justify="space-between" align="center" wrap="wrap" gap="sm">
        <Text size="sm" c="slate.7" fw={500}>
          Results: {start} - {end} of {totalItems}
        </Text>

        <Select
          size="sm"
          radius="md"
          w={84}
          data={pageSizeOptions.map((value) => ({ value: String(value), label: String(value) }))}
          value={String(pageSize)}
          onChange={(value) => {
            if (!value || !onPageSizeChange) return;
            const parsed = Number(value);
            if (!Number.isNaN(parsed)) onPageSizeChange(parsed);
          }}
          disabled={!canChangePageSize}
          allowDeselect={false}
        />
      </Flex>
    </Box>
  );
}
